import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/i18n";

// Layouts
import { AdminLayout } from "./components/AdminLayout";
import { RoleGuard } from "./components/RoleGuard";

// Pages
import Login from "./pages/Login";
import AdminDashboard from "./pages/AdminDashboard";
import AgentAnalytics from "./pages/analytics/AgentAnalytics";
import ProductAnalytics from "./pages/analytics/ProductAnalytics";
import DiscoveryAnalytics from "./pages/analytics/DiscoveryAnalytics";
import ReliabilityAnalytics from "./pages/analytics/ReliabilityAnalytics";
import CreatorAnalytics from "./pages/analytics/CreatorAnalytics";
import CreatorCodes from "./pages/CreatorCodes";
import SearchSuggestions from "./pages/SearchSuggestions";
import ItemList from "./pages/ItemList";
import ItemForm from "./pages/ItemForm";
import ShoesList from "./pages/ShoesList";
import Notes from "./pages/Notes";
import Operations from "./pages/Operations";
import Settings from "./pages/Settings";
import NotFound from "./pages/NotFound";


const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />
              
              {/* Redirect root to admin dashboard or login */}
              <Route path="/" element={<Navigate to="/admin" replace />} />
              
              {/* Protected Admin Routes */}
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="analytics/agents" element={<AgentAnalytics />} />
                <Route path="analytics/products" element={<ProductAnalytics />} />
                <Route path="analytics/discovery" element={<DiscoveryAnalytics />} />
                <Route path="analytics/reliability" element={<ReliabilityAnalytics />} />
                <Route path="analytics/creators" element={<CreatorAnalytics />} />
                <Route path="creators" element={<RoleGuard required="owner"><CreatorCodes /></RoleGuard>} />
                <Route path="search-suggestions" element={<RoleGuard required="owner"><SearchSuggestions /></RoleGuard>} />
                <Route path="clothinglist" element={<RoleGuard required="owner"><ItemList /></RoleGuard>} />
                <Route path="clothinglist/new" element={<RoleGuard required="owner"><ItemForm /></RoleGuard>} />
                <Route path="clothinglist/:id" element={<RoleGuard required="owner"><ItemForm /></RoleGuard>} />
                <Route path="shoes" element={<RoleGuard required="owner"><ShoesList /></RoleGuard>} />
                <Route path="shoes/new" element={<RoleGuard required="owner"><ItemForm /></RoleGuard>} />
                <Route path="shoes/:id" element={<RoleGuard required="owner"><ItemForm /></RoleGuard>} />

                <Route path="notes" element={<Notes />} />
                <Route path="operations" element={<Operations />} />

                <Route path="settings" element={<RoleGuard required="owner"><Settings /></RoleGuard>} />
              </Route>
              
              {/* 404 Not Found */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
